import { motion } from "framer-motion";
import {
  Lightbulb, Shield, DollarSign, ClipboardCheck, Rocket, GlobeLock, PiggyBank, Users2,
  AlertTriangle, Handshake, Globe, Zap, ScanEye, CheckCircle2, RefreshCw, TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

const benefitIconMap: Record<string, LucideIcon> = {
  "Facilitate product development": Lightbulb,
  "Improve efficiency & reduce risk": Shield,
  "Support funding & partnerships": DollarSign,
  "Prepare for audits & inspections": ClipboardCheck,
  "Enable faster time to market": Rocket,
  "Global expansion ready": GlobeLock,
  "Mitigate long-term costs": PiggyBank,
  "Align your team around a clear roadmap": Users2,
  "Accelerated progress": Rocket,
  "Reduced rework": AlertTriangle,
  "Lower Risk": Shield,
  "Investor confidence": DollarSign,
  "Integrated partnership": Handshake,
  "Lower compliance risk through demonstrable control": Lightbulb,
  "Faster remediation and more predictable delivery": Zap,
  "Digitally enabled compliance and efficiency": ScanEye,
  "Sustained readiness, not one-off fixes": CheckCircle2,
  "Multi-country access": Globe,
  "Reduced uncertainty": Shield,
  "First-time-right approach": Rocket,
  "Regulatory certainty": Shield,
  "Faster time to market": Rocket,
  "Single European partner": Handshake,
  "Tailored regulatory strategy": Lightbulb,
  "Expert guidance on procedures": ClipboardCheck,
  "In-depth regulatory knowledge": Shield,
  "Unified drug–diagnostic strategy": Handshake,
  "Portfolio optimization": TrendingUp,
  "Any type of product": Globe,
  "Tailor-made solutions": Lightbulb,
  "Structured EU and UK Pathway": Globe,
  "Optimized Documentation Transition": ClipboardCheck,
  "FDA-to-MDR expertise": Shield,
  "NMPA-to-MDR expertise": Shield,
};

interface Props {
  solution: SolutionData;
  onCtaClick?: () => void;
}

const SolutionBenefits = ({ solution }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  if (solution.benefits.length === 0) return null;

  return (
    <Section bg={solution.benefitsBg}>
      <SectionHeader
        label={solution.benefitsLabel || t("keyBenefits")}
        title={solution.benefitsTitle || t("whatYouGain")}
        icon={CheckCircle2}
      />

      <div className={`grid gap-5 ${solution.benefits.length === 4 ? 'sm:grid-cols-2 max-w-3xl' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
        {solution.benefits.map((b, i) => {
          const BenefitIcon = benefitIconMap[b.title] || CheckCircle2;
          return (
            <motion.div
              key={i}
              className="bg-card border border-border rounded-xl p-6 group hover:border-primary/30 hover:shadow-md transition-all duration-200"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
            >
              <div className="mb-4">
                <BenefitIcon size={32} className="text-primary" strokeWidth={1.4} />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1.5">{b.title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
};

export default SolutionBenefits;
